/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { Provider, Viewport } from "@radix-ui/react-toast";
import type {
  ComponentProps,
  Dispatch,
  ReactNode,
  SetStateAction,
} from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  numericPercentages,
  numericPixels,
  spacing,
  zIndices,
} from "../../../global/emotion";
import type { IconElement } from "../../components/_base/icons/Icon/Icon";
import { Toast } from "../../components/refactor/Toast/Toast.emotion";

export type ToastProviderProps = ComponentProps<typeof Provider>;

export type ToastOptions = {
  durationMs?: number;
  icon?: IconElement;
};

export type ToastFunction = (
  content: ReactNode,
  options?: ToastOptions,
) => void;

const viewportStyle: Interpolation = {
  bottom: spacing.m,
  display: "flex",
  flexDirection: "column",
  height: "auto",
  left: numericPercentages[50],
  maxWidth: "400px",
  placeContent: "center",
  placeItems: "center",
  position: "fixed",
  rowGap: spacing.m,
  transform: `translateX(-50%)`,
  transition: "bottom 250ms ease-out",
  width: `calc(100vw - ${spacing.m})`,
  zIndex: zIndices.toast,
};
const withTabBarStyle: Interpolation = {
  bottom: `calc(${spacing.xxl} + ${spacing.m})`,
};
const withPlayerStyle: Interpolation = {
  bottom: `calc(${numericPixels[96]} + ${spacing.m})`,
};
const withBothStyle: Interpolation = {
  bottom: `calc(${spacing.xxl} + ${numericPixels[64]} + ${spacing.m})`,
};

const TOAST_DEFAULT_DURATION_MS = 2500;
const TOAST_EXIT_ANIMATION_BUFFER_MS = 1500;

const toastContext = createContext<{
  setHasPlayer: Dispatch<SetStateAction<boolean>>;
  setHasTabBar: Dispatch<SetStateAction<boolean>>;
  toast: ToastFunction;
}>({
  setHasPlayer: () => {},
  setHasTabBar: () => {},
  toast: () => {},
});

export const useToastPositioning = () => useContext(toastContext);

export const useToast = (): ToastFunction => useContext(toastContext).toast;

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const t = useTranslations();
  const [hasPlayer, setHasPlayer] = useState<boolean>(false);
  const [hasTabBar, setHasTabBar] = useState<boolean>(false);
  const [toasts, setToasts] = useState<{ id: number; node: ReactNode }[]>([]);
  const nextIdRef = useRef(0);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const toast = useCallback<ToastFunction>((content, options) => {
    const id = nextIdRef.current++;
    const durationMs = options?.durationMs ?? TOAST_DEFAULT_DURATION_MS;
    setToasts((prev) => [
      ...prev,
      {
        id,
        node: (
          <Toast key={id} icon={options?.icon} durationMs={durationMs}>
            {content}
          </Toast>
        ),
      },
    ]);
    const handle = setTimeout(() => {
      setToasts((prev) => prev.filter((entry) => entry.id !== id));
      timeoutsRef.current.delete(handle);
    }, durationMs + TOAST_EXIT_ANIMATION_BUFFER_MS);
    timeoutsRef.current.add(handle);
  }, []);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const styleMemo = useMemo(() => {
    const style: Interpolation[] = [viewportStyle];
    if (hasPlayer && hasTabBar) style.push(withBothStyle);
    else if (hasPlayer) style.push(withPlayerStyle);
    else if (hasTabBar) style.push(withTabBarStyle);

    return style;
  }, [hasPlayer, hasTabBar]);

  return (
    <toastContext.Provider value={{ setHasPlayer, setHasTabBar, toast }}>
      <Provider label={t("web_toast_section_description")}>
        {children}

        {toasts.map((entry) => entry.node)}

        <Viewport label={t("web_toast_section_description")} asChild>
          <ol css={styleMemo} />
        </Viewport>
      </Provider>
    </toastContext.Provider>
  );
};
